import React from 'react';

import { RowLabelPicker } from '../../../../components-ui/RowLabelPicker';
import { Skeleton } from '../../../../components-ui/Skeleton';
import { labelColumnLabel } from '../../../../utils/contentLabelColumns';
import type { LabelCategory } from '../../../settings/labels/types';

import type { ContentLabelCellRow } from './contentLabelCells';

/**
 * One per-category label cell of the portfolio Content tab (fig4).
 *
 * WIRING, not a new picker. `components-ui/RowLabelPicker` already draws
 * exactly the four states fig4 shows — one chip, candidate chips plus a warning
 * triangle, a locked chip, "+ Add label" — and it carries the only
 * a11y-reviewed busy-picker behaviour in the codebase (a focusable
 * `aria-disabled` trigger, a controlled open that refuses while a write is in
 * flight). A second implementation would be the same component with a different
 * set of bugs.
 */
export type ContentLabelCellProps = {
  category: LabelCategory;
  /** The row as the server classified it — see `contentLabelRow`, which always answers one. */
  row: ContentLabelCellRow;
  /** The page's assignments read has not answered yet. */
  loading?: boolean;
  /** A write for THIS category is in flight: every picker in the column holds. */
  busy?: boolean;
  onAssign: (contentLabelCategoryId: string, contentLabelCategoryValueId: string, contentIds: string[]) => void;
  /** Category-wide: the write carries no value id. Reached from "Remove manual assignment". */
  onRemove: (contentLabelCategoryId: string, contentIds: string[]) => void;
};

export function ContentLabelCell({
  category,
  row,
  loading = false,
  busy = false,
  onAssign,
  onRemove,
}: ContentLabelCellProps): JSX.Element {
  /**
   * NOT "+ Add label" while the read is in flight. That pill is a live
   * affordance — a click opens a menu whose items write — so drawing it over a
   * row whose assignments have not landed invites a person to label a content
   * that may already be labelled.
   */
  if (loading) {
    return <Skeleton data-testid="content-label-cell-loading" className="h-6 w-24" />;
  }

  const contentIds = [row.content.contentId];
  /** The same string the column header is captioned with, so the two cannot drift. */
  const columnNoun = labelColumnLabel(category);

  return (
    <RowLabelPicker
      category={category}
      row={row}
      columnNoun={columnNoun}
      busy={busy}
      onAssign={(labelId) => onAssign(category.labelCategoryId, labelId, contentIds)}
      onReset={() => onRemove(category.labelCategoryId, contentIds)}
    />
  );
}
